const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * The Bearer fallback for the auth cookie.
 *
 * The cookie is the primary credential, but it is cross-site here — the app
 * and the API sit on different domains — so Safari's ITP and any browser
 * blocking third-party cookies drop it. Callers that only sent
 * `credentials: "include"` reached the API as anonymous, which is how a
 * signed-in attorney was asked for a first name the logged-in form does not
 * even show.
 */
function bearerHeaders() {
  try {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (e) {
    // Private browsing with storage blocked — the cookie is all we have.
    return {};
  }
}

export async function submitDirectoryListing(source, data) {
  const {
    firstName = "",
    lastName = "",
    email = "",
    publishEmail = false,
    publicEmail = "",
    phone = "",
    publishPhone = false,
    city = "",
    state = "",
    firmName = "",
    yearsInPractice = "",
    role = "",
    practiceAreas = [],
    practiceAreaOther = "",
    freeConsultation = false,
    website = "",
    publicList = false,
  } = data;

  const response = await fetch(`${API_URL}/directory`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...bearerHeaders() },
    credentials: "include",
    body: JSON.stringify({
      source,
      firstName,
      lastName,
      email,
      publishEmail,
      publicEmail,
      phone,
      publishPhone,
      city,
      state,
      firmName,
      yearsInPractice,
      role,
      practiceAreas: Array.isArray(practiceAreas)
        ? practiceAreas
        : Array.from(practiceAreas || []),
      practiceAreaOther,
      freeConsultation,
      website,
      publicList,
    }),
  });

  const resData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      resData.error || resData.message || "Failed to submit directory listing.",
    );
    // e.g. DUPLICATE_APPROVED, so the caller can offer a link to log in.
    error.code = resData.code || "";
    throw error;
  }

  return resData;
}

export async function getPublicDirectoryListings(params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  // Scoped to the practice-area fields — see the consumer "Other practice
  // area" box in FindAttorney.
  if (params.practiceAreaSearch)
    query.set("practiceAreaSearch", params.practiceAreaSearch);
  if (params.practiceArea) query.set("practiceArea", params.practiceArea);
  if (params.role) query.set("role", params.role);
  if (params.state) query.set("state", params.state);
  if (params.page) query.set("page", params.page);
  if (params.limit) query.set("limit", params.limit);

  const response = await fetch(`${API_URL}/directory?${query.toString()}`);
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to fetch directory listings.");
  }

  return resData;
}

export async function getAdminDirectoryListings(params = {}, authHeaders = {}) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.search) query.set("search", params.search);
  if (params.page) query.set("page", params.page);
  if (params.limit) query.set("limit", params.limit);

  const response = await fetch(`${API_URL}/directory/admin/listings?${query.toString()}`, {
    headers: {
      "Content-Type": "application/json",
      ...bearerHeaders(),
      ...authHeaders,
    },
    credentials: "include",
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to fetch admin directory listings.");
  }

  return resData;
}

export async function updateDirectoryListingStatus(id, status, authHeaders = {}) {
  const response = await fetch(`${API_URL}/directory/admin/listings/${id}/status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...bearerHeaders(),
      ...authHeaders,
    },
    credentials: "include",
    body: JSON.stringify({ status }),
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to update listing status.");
  }

  return resData;
}

export async function deleteDirectoryListing(id, authHeaders = {}) {
  const response = await fetch(`${API_URL}/directory/admin/listings/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...bearerHeaders(),
      ...authHeaders,
    },
    credentials: "include",
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to delete listing.");
  }

  return resData;
}

export async function verifyDirectoryListing(token) {
  const response = await fetch(`${API_URL}/directory/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to verify this listing.");
  }

  return resData;
}

/**
 * Completes the one-time activation link from the approval email by setting
 * the directory account's first password.
 */
export async function activateDirectoryAccount(token, password) {
  const response = await fetch(`${API_URL}/directory/activate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to activate this account.");
  }

  return resData;
}

/**
 * The signed-in user's own directory listing, or null when they have none.
 * Used to route "Log in to Directory" and to render My Directory Listing.
 */
export async function getMyDirectoryListing(authHeaders = {}) {
  const response = await fetch(`${API_URL}/directory/me`, {
    headers: {
      "Content-Type": "application/json",
      ...bearerHeaders(),
      ...authHeaders,
    },
    credentials: "include",
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "Failed to load your listing.");
  }

  return resData.listing || null;
}

/**
 * Update the signed-in attorney's own listing. Approved listings stay
 * approved: these edits go live immediately, with no second review.
 */
export async function updateMyDirectoryListing(updates, authHeaders = {}) {
  const response = await fetch(`${API_URL}/directory/me`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...bearerHeaders(),
      ...authHeaders,
    },
    credentials: "include",
    body: JSON.stringify(updates),
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    // A photo travels as base64 inside this body, so an oversized one is
    // refused by the proxy before the API sees it — and the reply is an HTML
    // error page, not JSON, so resData is empty and the caller used to show a
    // flat "Failed to update your listing."
    if (response.status === 413) {
      throw new Error(
        "That photo is too large to upload. Please choose a smaller image.",
      );
    }
    throw new Error(resData.error || "Failed to update your listing.");
  }

  return resData;
}

/**
 * Suppress an address from future LawFusion marketing email. The token from
 * the unsubscribe link is the only credential involved.
 */
export async function unsubscribeFromMarketing(token) {
  const response = await fetch(`${API_URL}/directory/unsubscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const resData = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(resData.error || "We could not unsubscribe this address.");
  }

  return resData;
}
